@import "~@esri/calcite-design-tokens/dist/scss/core";

/**
 * CSS Custom Properties
 *
 * These properties can be overridden using the component's tag as selector.
 *
 * @prop --calcite-modal-content-background: Specifies the background color of content placed in the `content` slot.
 * @prop --calcite-modal-content-padding: Specifies the padding of the modal `content` slot.
 * @prop --calcite-modal-scrim-background: Specifies the background color of the modal scrim.
 * @prop --calcite-modal-width: Specifies a width of the modal, using `px`, `em`, `rem`, `vw`, or `%`. Will never exceed the width of the viewport. Will not apply if `fullscreen` if set.
 * @prop --calcite-modal-height: Specifies a height of the modal, using `px`, `em`, `rem`, `vh`, or `%`. Will never exceed the height of the viewport. Will not apply if `fullscreen` if set.
 *
 */

:host {
  --calcite-modal-scrim-background: #{rgba($calcite-color-neutral-blk-240, $calcite-opacity-85)};
}

:host {
  @apply absolute flex inset-0 opacity-0 z-overlay;
  visibility: hidden !important;
  transition: visibility 0ms linear var(--calcite-internal-animation-timing-slow),
    opacity var(--calcite-internal-animation-timing-slow) $easing-function;
  // the modal should always use a dark scrim, regardless of light / dark mode - matches value in global.scss
  --calcite-modal-scrim-background-internal: #{rgba($calcite-color-neutral-blk-240, $calcite-opacity-85)};
}

.content-top[hidden],
.content-bottom[hidden] {
  @apply hidden;
}

.container {
  @apply text-color-2
    fixed
    inset-0
    flex
    items-center
    justify-center
    overflow-y-hidden
    opacity-0
    z-overlay;
  visibility: hidden !important;
  transition: visibility 0ms linear var(--calcite-internal-animation-timing-slow),
    opacity var(--calcite-internal-animation-timing-slow) $easing-function;
}

:host([scale="s"]) {
  --calcite-modal-padding-internal: theme("spacing.3");
  --calcite-modal-padding-large-internal: theme("spacing.4");
  --calcite-modal-title-text-internal: theme("fontSize.1h");
  --calcite-modal-content-text-internal: theme("fontSize.n1");
}

:host([scale="m"]) {
  --calcite-modal-padding-internal: theme("spacing.4");
  --calcite-modal-padding-large-internal: theme("spacing.5");
  --calcite-modal-title-text-internal: theme("fontSize.2h");
  --calcite-modal-content-text-internal: theme("fontSize.0");
}

:host([scale="l"]) {
  --calcite-modal-padding-internal: theme("spacing.5");
  --calcite-modal-padding-large-internal: theme("spacing.6");
  --calcite-modal-title-text-internal: theme("fontSize.3h");
  --calcite-modal-content-text-internal: theme("fontSize.1");
}

.scrim {
  --calcite-scrim-background: var(--calcite-modal-scrim-background, var(--calcite-color-transparent-scrim));
  @apply fixed inset-0 flex overflow-y-hidden;
}

.modal {
  @apply shadow-2-sm
    bg-foreground-1
    pointer-events-none
    float-none
    m-6
    box-border
    flex
    w-full
    flex-col
    overflow-hidden
    rounded
    opacity-0
    z-modal;
  -webkit-overflow-scrolling: touch;
  visibility: hidden;
  transition: transform var(--calcite-internal-animation-timing-slow) $easing-function,
    visibility 0ms linear var(--calcite-internal-animation-timing-slow),
    opacity var(--calcite-internal-animation-timing-slow) $easing-function;

  --calcite-modal-hidden-position: translate3d(0, 20px, 0);
  --calcite-modal-shown-position: translate3d(0, 0, 0);

  &--opening {
    &-idle {
      transform: var(--calcite-modal-hidden-position);
    }
    &-active {
      transform: var(--calcite-modal-shown-position);
    }
  }

  &--closing {
    &-idle {
      transform: var(--calcite-modal-shown-position);
    }
    &-active {
      transform: var(--calcite-modal-hidden-position);
    }
  }
}

:host([opened]) {
  @apply opacity-100;
  visibility: visible !important;
  transition-delay: 0ms;
}

.container--open {
  @apply opacity-100;
  visibility: visible !important;
  transition-delay: 0ms;
  .modal {
    @apply pointer-events-auto visible opacity-100;
    transition: transform var(--calcite-internal-animation-timing-slow) $easing-function, visibility 0ms linear,
      opacity var(--calcite-internal-animation-timing-slow) $easing-function,
      max-inline-size var(--calcite-internal-animation-timing-slow) $easing-function,
      max-block-size var(--calcite-internal-animation-timing-slow) $easing-function;
    transition-delay: 0ms;
  }
}

/**
 * Header
 */
.header {
  @apply bg-foreground-1
    border-color-3
    flex
    min-w-0
    max-w-full
    rounded-t
    border-0
    border-b
    border-solid
    z-header;
  flex: 0 0 auto;
}

.close {
  @apply text-color-3
    transition-default
    focus-base
    order-2
    m-0
    cursor-pointer
    appearance-none
    border-none
    bg-transparent;
  padding-block: var(--calcite-modal-padding-internal);
  padding-inline: var(--calcite-modal-padding-internal);
  flex: 0 0 auto;
  calcite-icon {
    @apply pointer-events-none;
    vertical-align: -2px;
  }
  &:focus {
    @apply focus-inset;
  }
  &:hover,
  &:focus,
  &:active {
    @apply bg-foreground-2 text-color-1;
  }
}

.title {
  @apply order-1 flex min-w-0 items-center;
  flex: 1 1 auto;
  padding-block: var(--calcite-modal-padding-internal);
  padding-inline: var(--calcite-modal-padding-large-internal);
}

@include slotted("header", "*") {
  @apply text-color-1 m-0 font-normal;
  font-size: var(--calcite-modal-title-text-internal);
}

/**
 * Content area
 */
.content {
  @apply relative box-border block h-full overflow-auto p-0;
  background-color: var(--calcite-modal-content-background, theme("colors.background.foreground.1"));
  max-block-size: 100%;
  padding: var(--calcite-modal-content-padding, var(--calcite-modal-padding-internal));
}

.content-top,
.content-bottom {
  @apply bg-foreground-1 border-color-3 border-solid border-0 z-header flex;
  flex: 0 0 auto;
  padding: var(--calcite-modal-padding-internal);
}

.content-top {
  @apply min-w-0 max-w-full border-b;
}

.content-bottom {
  @apply mt-auto box-border w-full justify-between border-t;
}

.content-top:not(.header ~ .content-top) {
  @apply rounded-t;
}

.content-bottom:not(.content-bottom ~ .footer),
.content--no-footer {
  @apply rounded-b;
}

@include slotted("content", "*") {
  font-size: var(--calcite-modal-context-text-internal);
}

/**
 * Footer
 */
.footer {
  @apply bg-foreground-1
    border-color-3
    mt-auto
    box-border
    flex
    w-full
    justify-between
    rounded-b
    border-0
    border-t
    border-solid
    z-header;
  flex: 0 0 auto;
  padding-block: var(--calcite-modal-padding-internal);
  padding-inline: var(--calcite-modal-padding-large-internal);
}

.footer--hide-back .back,
.footer--hide-secondary .secondary {
  @apply hidden;
}

.back {
  @apply block;
  margin-inline-end: auto;
}

.secondary {
  @apply mx-1 block;
}

slot[name="primary"] {
  @apply block;
}

/**
 * Sizes
 */
@mixin modal-size($size, $width) {
  :host([width-scale="#{$size}"]) .modal {
    @apply max-h-full max-w-full;
    inline-size: var(--calcite-modal-width, $width);
    block-size: var(--calcite-modal-height, auto);
  }

  @media screen and (max-width: $width + 2 * $baseline) {
    :host([width-scale="#{$size}"]) {
      .modal {
        @apply m-0 h-full max-h-full w-full max-w-full;
      }
      .content {
        flex: 1 1 auto;
        max-block-size: unset;
      }
    }
    :host([width-scale="#{$size}"][docked]) .container {
      align-items: flex-end;
    }
  }
}

:host([width="small"]) .modal {
  @apply w-auto;
}

@include modal-size("s", 32rem);
@include modal-size("m", 48rem);
@include modal-size("l", 94rem);

/**
 * Fullscreen
 */
:host([fullscreen]) {
  .modal {
    @apply m-0 max-h-full max-w-full h-full w-full rounded-none;
    --calcite-modal-hidden-position: translate3D(0, 20px, 0) scale(0.95);
    --calcite-modal-shown-position: translate3D(0, 0, 0) scale(1);
  }
  .content {
    @apply max-h-full;
    flex: 1 1 auto;
  }
}

:host([opened][fullscreen]) {
  .header,
  .footer,
  .content-top,
  .content-bottom {
    border-radius: 0;
  }
}

/**
 * Docked
 */
:host([docked]) {
  .modal {
    block-size: var(--calcite-modal-height, auto);
  }
  .content {
    @apply h-auto;
    flex: 1 1 auto;
  }
}

/**
 * Kinds
 */
:host([kind="brand"]) .modal {
  @apply border-color-brand;
}

:host([kind="danger"]) .modal {
  @apply border-color-danger;
}

:host([kind="info"]) .modal {
  @apply border-color-info;
}

:host([kind="success"]) .modal {
  @apply border-color-success;
}

:host([kind="warning"]) .modal {
  @apply border-color-warning;
}

:host([kind="brand"]),
:host([kind="danger"]),
:host([kind="info"]),
:host([kind="success"]),
:host([kind="warning"]) {
  .modal {
    @apply border-0 border-t-4 border-solid;
  }
  .header,
  .content-top {
    @apply rounded rounded-b-none;
  }
}

/**
 * Tablet
 */
@media screen and (max-width: $viewport-medium) {
  @include slotted("header", "content-top", "*") {
    @apply text-1;
  }
  .footer,
  .content-bottom {
    @apply sticky bottom-0;
  }
}

/**
 * Mobile
 */
@media screen and (max-width: $viewport-small) {
  .footer,
  .content-bottom {
    @apply flex-col;
  }
  .back,
  .secondary {
    @apply m-0 mb-1;
  }
}

/**
 * Conditional styles for when Modal is slotted in Shell
 */

.container.slotted-in-shell {
  position: absolute;
  pointer-events: auto;
  calcite-scrim {
    position: absolute;
  }
}

@include base-component();
